import React, { useState, useEffect, useRef } from 'react';
import { TextField, Grid, FormControl, InputLabel, Select, MenuItem, FormHelperText } from '@mui/material';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import FormContainer from '../../components/FormContainer';
import { addTaluka, updateTaluka, fetchStates, fetchDistrictsByState } from '../../utils/api';

const TalukaForm = ({ editTaluka = null, onSuccess }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);
  const [states, setStates] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [isLoadingStates, setIsLoadingStates] = useState(true);
  const [isLoadingDistricts, setIsLoadingDistricts] = useState(false);
  const isEditMode = !!editTaluka;
  const onSuccessTimerRef = useRef(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (onSuccessTimerRef.current != null) {
        clearTimeout(onSuccessTimerRef.current);
        onSuccessTimerRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    const loadInitialData = async () => {
      try {
        // Load states
        const statesResponse = await fetchStates();
        if (cancelled) return;
        setStates(statesResponse.data);
        setIsLoadingStates(false);

        // If editing and there's a district, load the districts for that state
        if (isEditMode && editTaluka.district?.state?.stateId) {
          setIsLoadingDistricts(true);
          const districtsResponse = await fetchDistrictsByState(editTaluka.district.state.stateId);
          if (cancelled) return;
          setDistricts(districtsResponse.data);
          setIsLoadingDistricts(false);
        }
      } catch (error) {
        console.error('Error loading form data:', error);
        if (!cancelled) {
          setErrorMessage('Failed to load form data. Please refresh the page.');
          setIsLoadingStates(false);
          setIsLoadingDistricts(false);
        }
      }
    };

    loadInitialData();
    return () => {
      cancelled = true;
    };
  }, [isEditMode, editTaluka]);

  const validationSchema = Yup.object({
    talukaName: Yup.string().required('Taluka name is required').max(100, 'Taluka name must be at most 100 characters'),
    stateId: Yup.number().required('State is required'),
    districtId: Yup.number().required('District is required'),
  });

  const formik = useFormik({
    initialValues: {
      talukaName: editTaluka ? editTaluka.talukaName : '',
      stateId: editTaluka && editTaluka.district?.state ? editTaluka.district.state.stateId : '',
      districtId: editTaluka ? editTaluka.district.districtId : '',
    },
    validationSchema,
    enableReinitialize: true,
    onSubmit: async (values, { resetForm }) => {
      setIsSubmitting(true);
      setSuccessMessage(null);
      setErrorMessage(null);

      try {
        const talukaData = {
          talukaName: values.talukaName,
          district: {
            districtId: values.districtId
          }
        };
        
        if (isEditMode) {
          await updateTaluka(editTaluka.talukaId, talukaData, {
            previousData: editTaluka,
            identifier: editTaluka.talukaName || values.talukaName
          });
          setSuccessMessage('Taluka updated successfully!');
        } else {
          await addTaluka(talukaData, {
            identifier: values.talukaName
          });
          setSuccessMessage('Taluka added successfully!');
          resetForm();
          setDistricts([]);
        }

        if (onSuccess) {
          if (onSuccessTimerRef.current != null) clearTimeout(onSuccessTimerRef.current);
          onSuccessTimerRef.current = setTimeout(() => {
            onSuccessTimerRef.current = null;
            onSuccess();
          }, 1500);
        }
      } catch (error) {
        console.error(`Error ${isEditMode ? 'updating' : 'adding'} taluka:`, error);
        setErrorMessage(error.response?.data?.message || `Failed to ${isEditMode ? 'update' : 'add'} taluka. Please try again.`);
      } finally {
        setIsSubmitting(false);
      }
    },
  });

  const handleStateChange = async (event) => {
    const stateId = event.target.value;
    formik.setFieldValue('stateId', stateId);
    formik.setFieldValue('districtId', '');
    
    if (stateId) {
      setIsLoadingDistricts(true);
      try {
        const response = await fetchDistrictsByState(stateId);
        if (!mountedRef.current) return;
        setDistricts(response.data);
      } catch (error) {
        console.error('Error fetching districts:', error);
        if (mountedRef.current) {
          setErrorMessage('Failed to load districts. Please try again.');
          setDistricts([]);
        }
      } finally {
        if (mountedRef.current) setIsLoadingDistricts(false);
      }
    } else {
      setDistricts([]);
    }
  };

  const handleCloseSnackbar = () => {
    setSuccessMessage(null);
    setErrorMessage(null);
  };

  if (isLoadingStates) {
    return <div>Loading states...</div>;
  }

  return (
    <FormContainer
      title={isEditMode ? "Edit Taluka" : "Add Taluka"}
      onSubmit={formik.handleSubmit}
      isSubmitting={isSubmitting}
      successMessage={successMessage}
      errorMessage={errorMessage}
      onCloseSnackbar={handleCloseSnackbar}
    >
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <FormControl 
            fullWidth 
            margin="normal"
            error={formik.touched.stateId && Boolean(formik.errors.stateId)}
          >
            <InputLabel id="state-select-label">State</InputLabel>
            <Select
              labelId="state-select-label"
              id="stateId"
              name="stateId"
              value={formik.values.stateId}
              onChange={handleStateChange}
              onBlur={formik.handleBlur}
              label="State"
            >
              {states.map((state) => (
                <MenuItem key={state.stateId} value={state.stateId}>
                  {state.stateName}
                </MenuItem>
              ))}
            </Select>
            {formik.touched.stateId && formik.errors.stateId && (
              <FormHelperText>{formik.errors.stateId}</FormHelperText>
            )}
          </FormControl>
        </Grid>
        <Grid item xs={12}>
          <FormControl 
            fullWidth 
            margin="normal"
            error={formik.touched.districtId && Boolean(formik.errors.districtId)}
            disabled={!formik.values.stateId || isLoadingDistricts}
          >
            <InputLabel id="district-select-label">District</InputLabel>
            <Select
              labelId="district-select-label"
              id="districtId"
              name="districtId"
              value={formik.values.districtId}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              label="District"
            >
              {districts.map((district) => (
                <MenuItem key={district.districtId} value={district.districtId}>
                  {district.districtName}
                </MenuItem>
              ))}
            </Select>
            {formik.touched.districtId && formik.errors.districtId && (
              <FormHelperText>{formik.errors.districtId}</FormHelperText>
            )}
          </FormControl>
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            id="talukaName"
            name="talukaName"
            label="Taluka Name"
            value={formik.values.talukaName}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.talukaName && Boolean(formik.errors.talukaName)}
            helperText={formik.touched.talukaName && formik.errors.talukaName}
            margin="normal"
            variant="outlined"
          />
        </Grid>
      </Grid>
    </FormContainer>
  );
};

export default TalukaForm; 